import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { signOut, User } from "firebase/auth";
import { auth } from "../firebase/firebase";
import { type AppRole } from "../services/authApi";
import { clearCachedRole } from "../services/roleCache";
import { clearRunPanelCache, clearTrendDashboardCache } from "../services/runPanelCache";
import { navItemsForRole } from "../nav/appNav";
import styles from "./Dashboard.module.css";

const UserAvatar: React.FC<{ user: User | null }> = ({ user }) => {
  const [imgBroken, setImgBroken] = useState(false);
  const label = user?.displayName || user?.email || "Account";
  const initials = useMemo(
    () =>
      label
        .split(/\s+/)
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "?",
    [label]
  );

  return (
    <div className={styles.navmenu_right_item}>
      <button type="button" className={styles.userAvatarBtn} aria-label="Account">
        {!imgBroken ? (
          <img
            className={styles.userAvatarImg}
            src={`${process.env.PUBLIC_URL || ""}/user-avatar.avif`}
            alt=""
            onError={() => setImgBroken(true)}
          />
        ) : (
          <span className={styles.userAvatarFallback}>{initials}</span>
        )}
      </button>
      <span className={styles.name}>{label}</span>
    </div>
  );
};

type AppShellProps = {
  currentUser: User | null;
  role: AppRole | null;
};

const AppShell: React.FC<AppShellProps> = ({ currentUser, role }) => {
  const navigate = useNavigate();

  const navItems = useMemo(() => (role ? navItemsForRole(role) : []), [role]);

  const handleLogout = async () => {
    try {
      clearCachedRole(currentUser?.uid);
      clearRunPanelCache();
      clearTrendDashboardCache();
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Failed to sign out", error);
    }
  };

  return (
    <div className="App">
      <main className="App-main">
        <div className={styles.navmenu}>
          <img
            className={styles.shellLogo}
            height={65}
            src={`${process.env.PUBLIC_URL || ""}/logo.svg`}
            alt="TrustForge AI logo"
          />
          <div className={styles.navmenu_right}>
            <UserAvatar user={currentUser} />
            <button
              type="button"
              className={styles.logoutBtn}
              onClick={handleLogout}
              aria-label="Log out"
            >
              <img height={22} src={`${process.env.PUBLIC_URL || ""}/Vector (2).svg`} alt="" />
            </button>
          </div>
        </div>
        <nav className={styles.shellNav} aria-label="Primary">
          <div className={styles.shellNavTrack}>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `${styles.shellNavLink} ${isActive ? styles.shellNavLinkActive : ""}`.trim()
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
        <section className={styles.shellRouteContainer}>
          <Outlet />
        </section>
      </main>
    </div>
  );
};

export default AppShell;
